import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../firebase";
import { Drawer, useMediaQuery, useTheme } from "@mui/material";
import { Users, BookOpen, FileText, HelpCircle } from "lucide-react";
import AnnouncementBell from "./AnnouncementBell";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "./ui/navigation-menu";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

// ============================================================================
// NAVBAR MODE CONFIGURATION
// ============================================================================
// To force dark mode on a page, add its path to DARK_MODE_PAGES
// Light mode is the default for all other pages
const DARK_MODE_PAGES = [
  "/download",
  "/login",
  "/login-success",
  "/hyperlinks-login",
  "/hyperlinks-onboard",
];

// Menu toggle icon component
function MenuToggleIcon({ open, className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line
        x1="4"
        y1="6"
        x2="20"
        y2="6"
        className={cn(
          "origin-center transition-transform duration-300",
          open && "translate-y-[6px] rotate-45"
        )}
      />
      <line
        x1="4"
        y1="12"
        x2="20"
        y2="12"
        className={cn(
          "transition-opacity duration-300",
          open && "opacity-0"
        )}
      />
      <line
        x1="4"
        y1="18"
        x2="20"
        y2="18"
        className={cn(
          "origin-center transition-transform duration-300",
          open && "-translate-y-[6px] -rotate-45"
        )}
      />
    </svg>
  );
}

// Featured link component for About Us
function FeaturedListItem({ title, description, href, onClick }) {
  return (
    <NavigationMenuLink asChild>
      <Link
        to={href}
        onClick={onClick}
        className="flex h-full w-full select-none flex-col justify-end rounded-lg bg-gradient-to-b from-gray-100 to-gray-50 p-5 no-underline outline-none transition-all hover:from-gray-200 hover:to-gray-100 focus:shadow-md"
      >
        <Users className="size-6 text-gray-700 mb-2" />
        <div className="mb-1 text-base font-semibold text-gray-900">{title}</div>
        <p className="text-sm leading-snug text-gray-600">{description}</p>
      </Link>
    </NavigationMenuLink>
  );
}

// Regular list item for dropdown menu
function ListItem({ title, description, icon: Icon, href, onClick }) {
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          to={href}
          onClick={onClick}
          className="group flex w-full items-start gap-3 rounded-lg p-3 no-underline outline-none transition-colors hover:bg-gray-100"
        >
          <div className="flex size-9 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white shadow-sm transition-colors group-hover:bg-gray-900 group-hover:border-gray-900">
            <Icon className="size-4 text-gray-600 transition-colors group-hover:text-white" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-900">{title}</span>
            <span className="text-xs text-gray-500 leading-snug">{description}</span>
          </div>
        </Link>
      </NavigationMenuLink>
    </li>
  );
}

// Detect user's operating system
function getOS() {
  const userAgent = window.navigator.userAgent;
  const platform = window.navigator.platform;

  if (platform.indexOf('Mac') !== -1 || userAgent.indexOf('Mac') !== -1) {
    return 'mac';
  }
  if (platform.indexOf('Win') !== -1 || userAgent.indexOf('Windows') !== -1) {
    return 'windows';
  }
  return 'other';
}

// Apple logo SVG component
function AppleLogo({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
    </svg>
  );
}

// Windows logo SVG component
function WindowsLogo({ className }) {
  return (
    <svg className={className} viewBox="0 0 448 512" fill="currentColor">
      <path d="M0 93.7l183.6-25.3v177.4H0V93.7zm0 324.6l183.6 25.3V268.4H0v149.9zm203.8 28L448 480V268.4H203.8v177.9zm0-380.6v180.1H448V32L203.8 65.7z"/>
    </svg>
  );
}

function Navbar({ forceDarkMode = false }) {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Detect OS for download button
  const [osName, setOsName] = useState('Download');

  useEffect(() => {
    setOsName(getOS());
  }, []);

  // Page detection
  const pathname = window.location.pathname;
  const isHomePage = pathname === "/";
  const isHyperlinksPage = pathname === "/123hyperlinks123";
  const isDarkModePage = DARK_MODE_PAGES.includes(pathname) || forceDarkMode;

  // Homepage scroll-based dark/light transition
  const [homeScrolledPastHero, setHomeScrolledPastHero] = useState(false);

  useEffect(() => {
    if (!isHomePage) return;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      // Transition at 70% of viewport height
      const transitionPoint = viewportHeight * 0.7;
      setHomeScrolledPastHero(scrollY > transitionPoint);
    };

    // Initial check
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomePage]);

  // Determine final dark mode state
  // Homepage: dark at top, light after scrolling past hero
  // Dark mode pages: always dark
  // Everything else: always light
  const isDarkMode = isHomePage
    ? !homeScrolledPastHero
    : isDarkModePage;

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  const scrollWithCallback = (el) => {
    const navbarHeight = 64;
    const elementPosition = el.offsetTop - navbarHeight;
    window.scrollTo({ top: elementPosition, left: 0, behavior: "smooth" });
    closeMobileMenu();
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  return (
    <>
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 w-full transition-all duration-500",
          isDarkMode
            ? "backdrop-blur-md"
            : "bg-white/95 backdrop-blur-sm shadow-sm"
        )}
        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', fontSize: '16px' }}
      >
        <nav className="mx-auto flex h-16 w-full max-w-[1200px] items-center justify-between px-6">
          {/* LEFT - Logo + Navigation */}
          <div className="flex items-center gap-8">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 222.68 211.78"
                className="h-[24px] w-auto"
              >
                <g>
                  <path
                    className={cn(
                      "transition-colors duration-500",
                      isDarkMode ? "fill-white" : "fill-black"
                    )}
                    d="m164.11,119.05c-.32.37-.65.73-1.01,1.07-.47.46-.97.87-1.51,1.28t-.01,0c-.26.2-.55.39-.84.57-.3.19-.59.35-.9.51-1.36.72-2.73,1.41-4.12,2.07-.46.23-.93.45-1.4.66-.93.43-1.88.84-2.84,1.23-1.42.61-2.86,1.18-4.32,1.72-.49.19-.98.36-1.48.52-.49.18-.98.35-1.48.5-10.35,3.46-21.41,5.33-32.9,5.33s-22.55-1.87-32.89-5.33c-.5-.16-.99-.33-1.48-.5-.5-.17-.99-.35-1.49-.52-1.44-.54-2.88-1.11-4.32-1.72-.94-.39-1.89-.81-2.83-1.23-.48-.21-.95-.43-1.41-.66-1.38-.66-2.75-1.34-4.11-2.07-.32-.16-.61-.33-.91-.51-.28-.19-.57-.37-.84-.57h-.01c-.54-.4-1.04-.82-1.51-1.28-.35-.34-.68-.7-.99-1.07-4.55-5.46-4.52-13.9.92-19.34.86-.85,1.81-1.6,2.82-2.19h.01c.39-.24.81-.46,1.23-.66.24-.11.48-.22.71-.32,2.37-.95,4.96-1.25,7.5-.86.35.04.71.12,1.06.2.36.09.71.18,1.06.28.83.26,1.66.6,2.45,1.02,1.3.69,2.63,1.34,3.99,1.95,9.46,4.31,19.98,6.7,31.03,6.7s21.57-2.39,31.03-6.7c1.35-.6,2.69-1.26,3.99-1.95,0-.01.01-.01.01-.01.79-.42,1.62-.75,2.44-1.01.36-.11.71-.2,1.07-.28.35-.08.71-.15,1.06-.2,2.54-.38,5.11-.09,7.49.86.24.09.48.21.72.32.43.2.84.42,1.23.66h.01c1.01.59,1.96,1.33,2.81,2.19,5.44,5.44,5.47,13.88.93,19.34Z"
                  />
                  <path
                    className={cn(
                      "transition-colors duration-500",
                      isDarkMode ? "fill-white" : "fill-black"
                    )}
                    d="m209.07,25.77c-3.46-4.51-7.5-8.56-12.01-12.04-.55-.43-1.1-.84-1.66-1.25-10.84-7.86-24.14-12.48-38.51-12.48h-91.11c-14.38,0-27.7,4.63-38.54,12.5-.55.4-1.09.81-1.64,1.22-4.5,3.47-8.54,7.53-12.01,12.04C5.11,36.81.05,50.62,0,65.56v40.2h0v14.95c0,15.04,5.07,28.92,13.59,40.02,3.45,4.51,7.51,8.57,12.01,12.04.55.43,1.09.83,1.63,1.22,10.5,7.63,23.33,12.22,37.2,12.5h.33c1.66,0,2.95,1.3,2.95,2.95v17.5c0,1.82,1.01,3.47,2.63,4.3.68.36,1.44.52,2.18.52,1.01,0,2-.31,2.84-.92l25.77-18.94c4.81-3.55,10.5-5.42,16.48-5.42h40.63c13.87-.28,26.7-4.87,37.2-12.5.54-.39,1.08-.8,1.63-1.22,4.5-3.48,8.56-7.53,12.01-12.04,8.52-11.09,13.59-24.98,13.59-40.02v-47.92h.01v-7.23c-.05-14.95-5.12-28.75-13.61-39.8Zm-17.98,86.37c0,16.02-8.47,30.06-21.19,37.87-6.75,4.15-14.72,6.55-23.23,6.55h-49.93,0s-20.73,0-20.73,0c-8.51,0-16.48-2.39-23.23-6.54-12.72-7.81-21.19-21.85-21.19-37.87v-11.29s-.02,0-.03,0v-25.71c0-8.02,2.12-15.53,5.84-22.02.37-.66.76-1.29,1.17-1.93.45-.71.93-1.4,1.43-2.08.41-.6.87-1.18,1.32-1.76,1.77-2.2,3.75-4.24,5.9-6.08,1.03-.86,2.1-1.69,3.2-2.48.76-.54,1.54-1.05,2.35-1.54,1.26-.78,2.57-1.5,3.91-2.14.72-.35,1.44-.67,2.19-.97,5.27-2.21,11.05-3.42,17.11-3.42h70.7c6.08,0,11.86,1.21,17.12,3.42.73.31,1.45.62,2.18.97,1.34.66,2.66,1.37,3.93,2.15.79.49,1.57,1,2.33,1.53,1.1.79,2.17,1.62,3.2,2.48,2.16,1.84,4.13,3.88,5.9,6.08.46.58.91,1.16,1.33,1.76.49.67.97,1.37,1.43,2.08.4.63.79,1.28,1.17,1.93,3.7,6.49,5.82,14,5.82,22.02v37Z"
                  />
                </g>
              </svg>
            </Link>

            {/* Desktop Navigation */}
            <NavigationMenu className="hidden md:flex items-center h-16">
              <NavigationMenuList className="gap-0 items-center m-0 h-16">
                {/* Pricing */}
                <NavigationMenuItem className="flex items-center h-16 p-0">
                  <NavigationMenuLink asChild>
                    <Link
                      to="/pricing"
                      onClick={scrollToTop}
                      className={cn(
                        "flex items-center px-3.5 transition-colors",
                        isDarkMode
                          ? "text-white hover:text-white/80"
                          : "text-black hover:text-black/70"
                      )}
                      style={{ fontSize: '15px', fontWeight: 500, lineHeight: 1 }}
                    >
                      Pricing
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                {/* Greek */}
                <NavigationMenuItem className="flex items-center h-16 p-0">
                  <NavigationMenuLink asChild>
                    <Link
                      to="/greek"
                      onClick={scrollToTop}
                      className={cn(
                        "flex items-center px-3.5 transition-colors",
                        isDarkMode
                          ? "text-white hover:text-white/80"
                          : "text-black hover:text-black/70"
                      )}
                      style={{ fontSize: '15px', fontWeight: 500, lineHeight: 1 }}
                    >
                      Greek
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                {/* Learn dropdown */}
                <NavigationMenuItem className="flex items-center h-16 p-0">
                  <NavigationMenuTrigger
                    className={cn(
                      "bg-transparent px-3.5",
                      isDarkMode
                        ? "text-white hover:text-white/80 hover:bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-white"
                        : "text-black hover:text-black/70 hover:bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-black"
                    )}
                    style={{ fontSize: '15px', fontWeight: 500, lineHeight: 1 }}
                  >
                    Learn
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="grid gap-3 p-4 w-[480px] lg:grid-cols-[.75fr_1fr]">
                      {/* Featured - About Us */}
                      <div className="row-span-3">
                        <FeaturedListItem
                          title="About Us"
                          description="Learn about our mission to help students succeed and the team behind EduTools."
                          href="/about"
                          onClick={scrollToTop}
                        />
                      </div>
                      {/* Right column items */}
                      <ul className="space-y-1">
                        <ListItem
                          title="Careers"
                          description="Coming soon"
                          icon={BookOpen}
                          href="/careers"
                          onClick={scrollToTop}
                        />
                        <ListItem
                          title="Blog"
                          description="Coming soon"
                          icon={FileText}
                          href="/blog"
                          onClick={scrollToTop}
                        />
                        <ListItem
                          title="Help Center"
                          description="Coming soon"
                          icon={HelpCircle}
                          href="/help-center"
                          onClick={scrollToTop}
                        />
                      </ul>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* FAQ */}
                <NavigationMenuItem className="flex items-center h-16 p-0">
                  <NavigationMenuLink asChild>
                    <HashLink
                      to="/#faq-section"
                      scroll={scrollWithCallback}
                      className={cn(
                        "flex items-center px-3.5 transition-colors",
                        isDarkMode
                          ? "text-white hover:text-white/80"
                          : "text-black hover:text-black/70"
                      )}
                      style={{ fontSize: '15px', fontWeight: 500, lineHeight: 1 }}
                    >
                      FAQ
                    </HashLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* RIGHT - Buttons */}
          <div className="flex items-center gap-2">
            {/* Announcement Bell - Only on HyperLinks page */}
            {isHyperlinksPage && (
              <div className="hidden md:block">
                <AnnouncementBell isDarkMode={isDarkMode} />
              </div>
            )}

            {/* Desktop Buttons */}
            <div className="hidden items-center gap-2 md:flex">
              <Button
                size="sm"
                asChild
                className={cn(
                  "h-8 px-3.5 text-sm font-medium transition-colors rounded-md",
                  isDarkMode
                    ? "bg-white text-black hover:bg-white/90"
                    : "bg-gray-900 text-white hover:bg-gray-800"
                )}
              >
                <Link to="/download" onClick={scrollToTop} className="flex items-center gap-2">
                  {osName === 'mac' && <AppleLogo className="size-4" />}
                  {osName === 'windows' && <WindowsLogo className="size-4" />}
                  {osName === 'mac' ? 'Download for macOS' : osName === 'windows' ? 'Download for Windows' : 'Download'}
                </Link>
              </Button>
            </div>

            {/* Mobile Announcement Bell */}
            {isHyperlinksPage && (
              <div className="flex items-center md:hidden">
                <AnnouncementBell isDarkMode={isDarkMode} />
              </div>
            )}

            {/* Mobile Menu Button */}
            <Button
              size="icon"
              variant="ghost"
              onClick={toggleMobileMenu}
              className={cn(
                "h-8 w-8 md:hidden transition-colors",
                isDarkMode
                  ? "text-white hover:bg-white/10"
                  : "text-gray-700 hover:bg-gray-100"
              )}
              aria-expanded={mobileMenuOpen}
              aria-label="Toggle menu"
            >
              <MenuToggleIcon open={mobileMenuOpen} className="size-5" />
            </Button>
          </div>
        </nav>
      </header>

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="bottom"
        open={mobileMenuOpen}
        onClose={closeMobileMenu}
        transitionDuration={400}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            height: "70vh",
            maxHeight: "70vh",
            background: "#ffffff",
            color: "#000000",
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
            boxShadow: "0 -8px 32px rgba(0, 0, 0, 0.3)",
            borderTop: "1px solid rgba(0, 0, 0, 0.1)",
          },
          "& .MuiBackdrop-root": {
            background: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <div className="flex h-full flex-col overflow-hidden p-4">
          {/* Handle bar */}
          <div className="flex justify-center pb-4">
            <div className="h-1 w-12 rounded-full bg-black/20" />
          </div>

          {/* Mobile Navigation Links */}
          <div className="flex-1 overflow-y-auto">
            <div className="flex w-full flex-col gap-y-1">
              {/* Main Links */}
              <Link
                to="/pricing"
                onClick={() => { scrollToTop(); closeMobileMenu(); }}
                className="flex items-center rounded-md p-3 text-sm font-medium text-gray-900 hover:bg-gray-100"
              >
                Pricing
              </Link>
              <Link
                to="/greek"
                onClick={() => { scrollToTop(); closeMobileMenu(); }}
                className="flex items-center rounded-md p-3 text-sm font-medium text-gray-900 hover:bg-gray-100"
              >
                Greek
              </Link>

              {/* Learn Section */}
              <div className="mt-3 border-t pt-3">
                <span className="px-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Learn</span>
                <Link
                  to="/about"
                  onClick={() => { scrollToTop(); closeMobileMenu(); }}
                  className="flex items-center gap-3 rounded-md p-3 text-sm font-medium text-gray-900 hover:bg-gray-100"
                >
                  <Users className="size-4 text-gray-500" />
                  About Us
                </Link>
                <Link
                  to="/careers"
                  onClick={() => { scrollToTop(); closeMobileMenu(); }}
                  className="flex items-center gap-3 rounded-md p-3 text-sm font-medium text-gray-900 hover:bg-gray-100"
                >
                  <BookOpen className="size-4 text-gray-500" />
                  Careers
                </Link>
                <Link
                  to="/blog"
                  onClick={() => { scrollToTop(); closeMobileMenu(); }}
                  className="flex items-center gap-3 rounded-md p-3 text-sm font-medium text-gray-900 hover:bg-gray-100"
                >
                  <FileText className="size-4 text-gray-500" />
                  Blog
                </Link>
                <Link
                  to="/help-center"
                  onClick={() => { scrollToTop(); closeMobileMenu(); }}
                  className="flex items-center gap-3 rounded-md p-3 text-sm font-medium text-gray-900 hover:bg-gray-100"
                >
                  <HelpCircle className="size-4 text-gray-500" />
                  Help Center
                </Link>
              </div>

              {/* FAQ */}
              <div className="mt-3 border-t pt-3">
                <HashLink
                  to="/#faq-section"
                  scroll={scrollWithCallback}
                  className="flex items-center rounded-md p-3 text-sm font-medium text-gray-900 hover:bg-gray-100"
                >
                  FAQ
                </HashLink>
              </div>
            </div>
          </div>

          {/* Mobile Action Buttons */}
          <div className="flex flex-col gap-2 border-t pt-4">
            <Button
              asChild
              className="w-full bg-gray-900 text-white hover:bg-gray-800"
            >
              <Link to="/download" onClick={() => { scrollToTop(); closeMobileMenu(); }} className="flex items-center justify-center gap-2">
                {osName === 'mac' && <AppleLogo className="size-4" />}
                {osName === 'windows' && <WindowsLogo className="size-4" />}
                {osName === 'mac' ? 'Download for macOS' : osName === 'windows' ? 'Download for Windows' : 'Download'}
              </Link>
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
}

export default Navbar;
